import React from "react"
import { AssetMap, Round } from "../types"

interface FAQModalProps {
  round: Round
  assets: AssetMap
  dismiss: () => void
}

export default function FAQModal({ round, assets, dismiss }: FAQModalProps) {
  return (
    <div id="round-faq-modal" className="modal modal--bottom-sheet-mobile show">
      <div className="modal-wrapper">
        <div className="modal-header">
          <div className="modal-header-inner">
            <div className="modal-title">FAQs</div>
            <a onClick={dismiss} className="modal-dismiss">
              <img src={assets.close} alt="Close" />
            </a>
          </div>
        </div>
        <div className="modal-main">
          <div className="container">
            <div className="faq-list">
              <ul>
                <li>
                  <h3>How do you play Spark Golf?</h3>
                  <p>Watch this video to learn how to play Spark Golf</p>
                  <p style={{ textAlign: "center", padding: "1rem 0" }}>
                    <a href="https://d2wwui9bam2a46.cloudfront.net/how-to-play-spark-golf.mp4" target="_blank">
                      <img
                        src="https://spark-golf-user-photos.imgix.net/how-to-play-spark-golf-title-screen.jpg?w=1140"
                        alt="YouTube"
                        width="380"
                      />
                    </a>
                  </p>
                </li>
                <li>
                  <h3>What do I do when I get to the course?</h3>
                  <p>
                    Check in with course staff and
                    {round.priceIncludesCart
                      ? "ask where the Spark Golf carts are located"
                      : "purchase a golf cart at the golf shop if desired"}
                    . Spark doesn't have a representative on-site but can always be texted at{" "}
                    <a href={`sms:${round.helpNumber.number}`}>{round.helpNumber.text}</a> or emailed at{" "}
                    <a href="mailto:help@spark.golf">help@spark.golf</a>.
                  </p>
                </li>
                <li>
                  <h3>What tee should I play?</h3>
                  <p>
                    Men will play the {round.tee.name} tees and women will play the forward tees. Seniors 60 years or
                    older can play one tee up from the men if they choose.
                  </p>
                </li>
                <li>
                  <h3>What if there is threatening weather?</h3>
                  <p>
                    The course staff makes the final decision if a round is to be cancelled. If this happens, the status
                    of the round will be updated in the Spark app, and you will be notified via email/SMS text. If you
                    have questions about the status of the round, please call the course directly
                    {round.course.phoneNumber ? (
                      <>
                        {" "}
                        at <a href={`tel:${round.course.phoneNumber.number}`}>{round.course.phoneNumber.text}</a>.
                      </>
                    ) : (
                      <></>
                    )}
                  </p>
                </li>
                <li>
                  <h3>What if my partner or I can't make it?</h3>
                  <p>
                    Since it's after the round registration deadline, you have 2 options. You can either invite a sub in
                    the Spark app to play for the person who can't make it or play as a single player team.
                  </p>
                </li>
                <li>
                  <h3>What is the format?</h3>
                  <p>
                    Each 2-person team is playing a net best ball format. Each player plays their own ball into the
                    hole. This is NOT a scramble. After each ball is holed, the team enters each player’s gross (actual)
                    score into the Spark app which calculates the net score for each player based on their Spark
                    Strokes. The team score used for the hole is the better of the 2 net scores.
                  </p>
                </li>
                <li>
                  <h3>What is the max score on a hole?</h3>
                  <p>
                    Net double bogey is the max score for each hole. If this is reached prior to the ball being holed,
                    the player should pick up to increase pace of play.
                  </p>
                </li>
                <li>
                  <h3>How are the winners decided?</h3>
                  <p>
                    The team with the lowest net score will win the round. Each team who plays in the round will receive
                    points towards their team's season points total. The amount of points awarded per round is based on
                    each team’s finish in that round with 1st place receiving the most points. The team with the most
                    cumulative points in their best 10 rounds at the end of the season will be league champs.
                  </p>
                </li>
                <li>
                  <h3>How many Spark Strokes do I get?</h3>
                  <p>
                    You'll see an orange dot(s) next to your name in the Spark app on the hole(s) where you're receiving
                    them. A “stroke” provides a player an advantage on a specific hole by reducing their actual or
                    “gross” score to calculate their “net” score on that hole.
                  </p>
                </li>
                <li>
                  <h3>What are the rules?</h3>
                  <p>
                    The #1 rule is to have fun! This is a social league so please relax. Also, please play fast. Spark
                    likes to keep things moving and play in 2 hours. Remember that max score is net double bogey. If you
                    reach this prior to the ball being holed, please pick up and go to the next hole.
                  </p>
                </li>
                <li>
                  <h3>What should I do if I hit it out of bounds or lose my ball?</h3>
                  <p>
                    We play out-of-bounds and lost ball as a lateral hazard, which means if you hit your ball into a
                    white stakes area or lose it, you may drop your ball and take a one stroke penalty.
                  </p>
                </li>
                <li>
                  <h3>Can I improve my lie?</h3>
                  <p>
                    If your ball is in the fairway, yes, but if it is in the rough or a bunker, than play it as it lies.
                  </p>
                </li>
                <li>
                  <h3>Are gimmies allowed?</h3>
                  <p>
                    Yes! To promote a fun, social atmosphere and improve pace of play, if your ball is less than length
                    of a standard putter away from the hole, pick it up, it's good! Make sure to count the gimmie as a
                    stroke when you input your score into the Spark app.
                  </p>
                </li>
                <li>
                  <h3>What are the prizes for each round?</h3>
                  <p>
                    Spark funds each round's skins pot with a $1 per player that plays in the round, up to $
                    {round.numberOfHoles} per round. The pot is then divided by the number of skins won in the round and
                    paid to the winning players in Spark credit. The credit can then be used for a future Spark
                    purchase.
                  </p>
                </li>
                <li>
                  <h3>What if the Spark app isn't working?</h3>
                  <p>
                    If the app stops working, please text us at{" "}
                    <a href={`sms:${round.helpNumber.number}`}>{round.helpNumber.text}</a> or email us at{" "}
                    <a href="mailto:help@spark.golf">help@spark.golf</a> ASAP. Also, please record your score on a
                    scorecard so you can input them once the app starts working again.
                  </p>
                </li>
                <li>
                  <h3>How do I get help?</h3>
                  <p>
                    Text us at <a href={`sms:${round.helpNumber.number}`}>{round.helpNumber.text}</a> or email us at{" "}
                    <a href="mailto:help@spark.golf">help@spark.golf</a>.
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-mask" onClick={dismiss} />
    </div>
  )
}
